/**
 * DialogCustomFields - Reusable dialog for viewing/managing custom fields for an entity.
 *
 * Shows active custom fields in a compact list with type & required badges,
 * plus an "Add Field" button that opens the existing DialogCustomColumn.
 * Saves changes to the backend and updates project context.
 *
 * Used by:
 * - CustomFieldsLink (inline in listing page headers)
 * - Settings > Custom Fields page
 */

// react
import { useContext, useEffect, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	CustomColumnDefinition,
	CustomColumnType,
	CustomFieldDefinition,
	CustomFieldEntityType,
	Project,
} from '../../../@types';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Switch,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	ArrowDownward as IconArrowDown,
	ArrowUpward as IconArrowUp,
	Close as IconClose,
	Edit as IconEdit,
} from '@mui/icons-material';

// existing single-field dialog
import { DialogCustomColumn } from '../../dialogs/DialogCustomColumn/DialogCustomColumn';

// Types

/** Entity keys that map to project custom column properties */
export type CustomFieldEntity =
	| 'activity'
	| 'beneficiary'
	| 'contract'
	| 'spendPlan'
	| 'supplier'
	| 'workPlan';

interface EntityMeta {
	label: string;
	apiEntityType: CustomFieldEntityType;
}

const ENTITY_META: Record<CustomFieldEntity, EntityMeta> = {
	activity: { label: 'Activity', apiEntityType: 'activity' },
	beneficiary: {
		label: 'Beneficiary',
		apiEntityType: 'beneficiary',
	},
	contract: { label: 'Contract', apiEntityType: 'contract' },
	spendPlan: {
		label: 'Spend Plan',
		apiEntityType: 'spend_plan',
	},
	supplier: { label: 'Supplier', apiEntityType: 'supplier' },
	workPlan: {
		label: 'Work Plan',
		apiEntityType: 'work_plan',
	},
};

const generateColumnId = () => `col_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Props

export interface DialogCustomFieldsProps {
	/** Which entity's custom fields to manage */
	entity: CustomFieldEntity;
	/** Whether the dialog is open */
	open: boolean;
	/** Called when the dialog should close */
	onClose: () => void;
}

// Helpers

const typeColor = (
	type: CustomColumnType
): 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' => {
	const map: Record<
		CustomColumnType,
		'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'
	> = {
		boolean: 'success',
		date: 'info',
		number: 'warning',
		select: 'secondary',
		text: 'primary',
	};
	return map[type] || 'default';
};

const typeLabel = (type: CustomColumnType, t: (k: string) => string): string =>
	t(`fieldType.${type}`);

const getColumnsFromProject = (
	project: Project,
	entity: CustomFieldEntity
): CustomColumnDefinition[] => {
	const meta = ENTITY_META[entity];
	const definitions = project.customFieldDefinitions || [];
	const definitionRow = definitions.find((d) => d.entityType === meta.apiEntityType);
	return definitionRow?.definitions || [];
};

// Component

export const DialogCustomFields = ({ entity, open, onClose }: DialogCustomFieldsProps) => {
	const { t } = useTranslation();
	const { project, setProject } = useContext(ContextProject);
	const meta = ENTITY_META[entity];
	const entityLabel = t(entity as any);

	// ── Local column state (synced from project when dialog opens) ────────
	const [columns, setColumns] = useState<CustomColumnDefinition[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	// ── Single-field edit dialog ──────────────────────────────────────────
	const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
	const [editingColumn, setEditingColumn] = useState<CustomColumnDefinition | null>(null);

	// Sync from project context when dialog opens
	useEffect(() => {
		if (open && project) {
			setColumns(getColumnsFromProject(project, entity));
			setSaveError(null);
		}
	}, [open, project, entity]);

	// Derived
	const activeColumns = useMemo(() => columns.filter((c) => c.isActive), [columns]);

	// ── Persist to backend ───────────────────────────────────────────────
	const save = async (updated: CustomColumnDefinition[]) => {
		if (!project) return;
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');

		setIsSaving(true);
		setSaveError(null);

		try {
			const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/v1\/?$/, '');
			const res = await fetch(`${apiUrl}/v2/custom-field-definitions`, {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
					User: String(idToken),
				},
				body: JSON.stringify({
					definitions: updated,
					entityType: meta.apiEntityType,
					projectId: project.id,
				}),
			});
			const result = await res.json();

			if (result.error) {
				setSaveError(result.error.message || t('dialogCustomFields.saveFailed'));
			} else if (setProject) {
				const nextDefinition = result.data as CustomFieldDefinition;
				const nextDefinitions = [
					...(project.customFieldDefinitions || []).filter((d) => d.id !== nextDefinition.id),
					nextDefinition,
				];
				setProject({
					...project,
					customFieldDefinitions: nextDefinitions,
				});
			}
		} catch {
			setSaveError(t('dialogCustomFields.saveFailed'));
		} finally {
			setIsSaving(false);
		}
	};

	// ── Handlers ─────────────────────────────────────────────────────────
	const handleAddField = () => {
		setEditingColumn(null);
		setFieldDialogOpen(true);
	};

	const handleEditField = (col: CustomColumnDefinition) => {
		setEditingColumn(col);
		setFieldDialogOpen(true);
	};

	const handleSaveField = (data: Omit<CustomColumnDefinition, 'id' | 'order'>) => {
		let updated: CustomColumnDefinition[];
		if (editingColumn) {
			updated = columns.map((c) => (c.id === editingColumn.id ? { ...c, ...data } : c));
		} else {
			updated = [...columns, { ...data, id: generateColumnId(), order: columns.length }];
		}
		setColumns(updated);
		setFieldDialogOpen(false);
		setEditingColumn(null);
		save(updated);
	};

	const handleToggleActive = (id: string) => {
		const updated = columns.map((c) => (c.id === id ? { ...c, isActive: !c.isActive } : c));
		setColumns(updated);
		save(updated);
	};

	const handleMoveUp = (idx: number) => {
		if (idx === 0) return;
		const updated = [...columns];
		[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
		updated.forEach((c, i) => (c.order = i));
		setColumns(updated);
		save(updated);
	};

	const handleMoveDown = (idx: number) => {
		if (idx === columns.length - 1) return;
		const updated = [...columns];
		[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
		updated.forEach((c, i) => (c.order = i));
		setColumns(updated);
		save(updated);
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
				<DialogTitle
					sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography variant="h6" component="span">
						{t('dialogCustomFields.title', { label: entityLabel })}
					</Typography>
					<IconButton onClick={onClose} size="small">
						<IconClose />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ py: 1 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 0.5 }}>
					{t('dialogCustomFields.description', { label: entityLabel.toLowerCase() })}
				</Typography>

					{saveError && (
						<Alert severity="error" sx={{ mb: 1 }} onClose={() => setSaveError(null)}>
							{saveError}
						</Alert>
					)}

					{columns.length === 0 ? (
						<Box sx={{ py: 4, textAlign: 'center' }}>
						<Typography color="text.secondary">{t('dialogCustomFields.noFields')}</Typography>
						</Box>
					) : (
						<Stack spacing={0}>
							{/* Column headers */}
							<Stack
								direction="row"
								alignItems="center"
								spacing={1}
								sx={{
									py: 0.75,
									px: 1,
									borderBottom: '2px solid',
									borderColor: 'divider',
									bgcolor: 'action.hover',
								}}>
								<Box sx={{ width: 32, flexShrink: 0 }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Order
									</Typography>
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Field Name
									</Typography>
								</Box>
								<Box sx={{ width: 72, textAlign: 'center' }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Type
									</Typography>
								</Box>
								<Box sx={{ width: 42, textAlign: 'center' }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Req
									</Typography>
								</Box>
								<Box sx={{ width: 48, textAlign: 'center' }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Active
									</Typography>
								</Box>
								<Box sx={{ width: 32, textAlign: 'center' }}>
									<Typography variant="caption" fontWeight={700} color="text.secondary">
										Edit
									</Typography>
								</Box>
							</Stack>
							{columns.map((col, idx) => {
								const globalIdx = columns.indexOf(col);
								return (
									<Stack
										key={col.id}
										direction="row"
										alignItems="center"
										spacing={1}
										sx={{
											py: 1,
											px: 1,
											borderBottom: '1px solid',
											borderColor: 'divider',
											opacity: col.isActive ? 1 : 0.55,
											'&:last-child': { borderBottom: 'none' },
										}}>
										{/* Reorder arrows */}
										<Tooltip title={t('dialogCustomFields.reorderField')} placement="left">
											<Stack direction="column" spacing={0} sx={{ flexShrink: 0 }}>
												<IconButton
													size="small"
													disabled={idx === 0 || isSaving}
													onClick={() => handleMoveUp(globalIdx)}
													sx={{ p: 0.25 }}>
													<IconArrowUp sx={{ fontSize: 16 }} />
												</IconButton>
												<IconButton
													size="small"
													disabled={idx === columns.length - 1 || isSaving}
													onClick={() => handleMoveDown(globalIdx)}
													sx={{ p: 0.25 }}>
													<IconArrowDown sx={{ fontSize: 16 }} />
												</IconButton>
											</Stack>
										</Tooltip>
										{/* Name + badges */}
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography
												variant="body2"
												fontWeight={600}
												noWrap
												sx={{ fontSize: '0.9375rem' }}>
												{col.name}
											</Typography>
										</Box>

										<Tooltip title={t('dialogCustomFields.fieldType', { type: typeLabel(col.type, t) })}>
											<Chip
												size="small"
												label={typeLabel(col.type, t)}
												color={typeColor(col.type)}
												variant="outlined"
												sx={{
													height: 22,
													fontSize: '0.75rem',
													width: 64,
													justifyContent: 'center',
												}}
											/>
										</Tooltip>

										<Tooltip
											title={col.isRequired ? t('dialogCustomFields.required') : t('dialogCustomFields.optional')}>
											<Box sx={{ width: 42, textAlign: 'center' }}>
												{col.isRequired ? (
													<Chip
														size="small"
													label={t('dialogCustomFields.columns.req')}
														color="error"
														sx={{ height: 22, fontSize: '0.75rem' }}
													/>
												) : (
													<Typography variant="caption" color="text.disabled">
														—
													</Typography>
												)}
											</Box>
										</Tooltip>

										{/* Toggle active */}
										<Tooltip
											title={
												col.isActive
													? t('dialogCustomFields.fieldVisible')
													: t('dialogCustomFields.fieldHidden')
											}>
											<Switch
												size="small"
												checked={col.isActive}
												disabled={isSaving}
												onChange={() => handleToggleActive(col.id)}
											/>
										</Tooltip>

										{/* Edit */}
										<Tooltip title={t('dialogCustomFields.editField')}>
											<IconButton
												size="small"
												disabled={isSaving}
												onClick={() => handleEditField(col)}>
												<IconEdit sx={{ fontSize: 18 }} />
											</IconButton>
										</Tooltip>
									</Stack>
								);
							})}
						</Stack>
					)}
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 1.5 }}>
					<Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
						{t('dialogCustomFields.activeCount', { count: activeColumns.length })}
					</Typography>
					<Button onClick={handleAddField} startIcon={<IconAdd />} variant="contained" size="small">
						{t('dialogCustomFields.addField')}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Single-field add/edit dialog (stacks on top) */}
			<DialogCustomColumn
				column={editingColumn}
				open={fieldDialogOpen}
				onClose={() => {
					setFieldDialogOpen(false);
					setEditingColumn(null);
				}}
				onSave={handleSaveField}
			/>
		</>
	);
};
